import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const EconomicImpact = () => {
  const revenueData = [
    { year: '2020', revenue: 3.2, families: 120 },
    { year: '2021', revenue: 4.8, families: 185 },
    { year: '2022', revenue: 6.1, families: 240 },
    { year: '2023', revenue: 7.5, families: 310 },
    { year: '2024', revenue: 8.4, families: 380 },
    { year: '2025', revenue: 9.8, families: 450 }
  ];

  const distributionData = [
    { name: 'Eco-Tourism Guides', value: 35, color: '#1b8a5b' },
    { name: 'Handicraft Cooperatives', value: 25, color: '#ffc107' },
    { name: 'Conservation Rangers', value: 20, color: '#02453e' },
    { name: 'Cultural Programs', value: 12, color: '#4a90a4' },
    { name: 'Research Support', value: 8, color: '#ff6b35' }
  ];

  const livelihoodPrograms = [
    {
      id: 1,
      name: "Eco-Tourism Guide Training",
      participants: 156,
      avgIncome: "₱18,500/month",
      jobs: 156,
      description: "Comprehensive training program creating certified eco-tourism guides",
      icon: "Compass"
    },
    {
      id: 2,
      name: "Sustainable Handicraft Cooperative",
      participants: 89,
      avgIncome: "₱15,200/month",
      jobs: 89,
      description: "Women-led cooperative producing eco-friendly handicrafts from sustainable materials",
      icon: "Package"
    },
    {
      id: 3,
      name: "Community Ranger Program",
      participants: 67,
      avgIncome: "₱22,000/month",
      jobs: 67,
      description: "Full-time conservation rangers from local communities",
      icon: "Shield"
    },
    {
      id: 4,
      name: "Organic Agriculture Initiative",
      participants: 124,
      avgIncome: "₱12,800/month",
      jobs: 124,
      description: "Sustainable farming practices supplying park facilities and local markets",
      icon: "Sprout"
    },
    {
      id: 5,
      name: "Cultural Heritage Tourism",
      participants: 45,
      avgIncome: "₱16,500/month",
      jobs: 45,
      description: "Indigenous-led cultural immersion experiences and traditional knowledge sharing",
      icon: "Users"
    },
    {
      id: 6,
      name: "Reforestation Employment",
      participants: 98,
      avgIncome: "₱14,000/month",
      jobs: 98,
      description: "Seasonal and full-time positions in native species reforestation projects",
      icon: "Trees"
    }
  ];

  const impactMetrics = [
    {
      label: "Total Economic Impact",
      value: "₱8.4M",
      change: "+18% YoY",
      icon: "TrendingUp",
      color: "text-success"
    },
    {
      label: "Families Benefited",
      value: "450",
      change: "+70 this year",
      icon: "Users",
      color: "text-info"
    },
    {
      label: "Jobs Created",
      value: "579",
      change: "+124 this year",
      icon: "Briefcase",
      color: "text-accent"
    },
    {
      label: "Avg. Income Increase",
      value: "165%",
      change: "vs. previous livelihood",
      icon: "ArrowUpCircle",
      color: "text-success"
    }
  ];

  return (
    <section className="py-16 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
            Economic Impact & Sustainable Livelihoods
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
            Conservation-driven economic opportunities transforming communities while protecting natural resources.
          </p>
        </div>

        {/* Impact Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {impactMetrics?.map((metric, index) => (
            <div key={index} className="bg-[var(--color-card)] p-6 rounded-lg shadow-conservation border border-[var(--color-border)]">
              <div className="flex items-center justify-between mb-3">
                <Icon name={metric?.icon} size={24} className={metric?.color} />
                <span className="text-sm text-success font-medium">{metric?.change}</span>
              </div>
              <div className="text-3xl font-bold text-[var(--color-foreground)] mb-1">{metric?.value}</div>
              <div className="text-sm text-[var(--color-muted-foreground)]">{metric?.label}</div>
            </div>
          ))}
        </div>

        {/* Revenue Growth Chart */}
        <div className="bg-[var(--color-card)] rounded-lg p-6 shadow-conservation mb-12 border border-[var(--color-border)]">
          <h3 className="text-xl font-bold text-[var(--color-foreground)] mb-6">Economic Growth (2020-2025)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="year" 
                stroke="var(--color-muted-foreground)"
                tick={{ fill: 'var(--color-foreground)' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                tick={{ fill: 'var(--color-foreground)' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
                labelStyle={{ color: 'var(--color-foreground)' }}
              />
              <Bar dataKey="revenue" name="Revenue (₱M)" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Livelihood Programs */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-[var(--color-foreground)] mb-6">Livelihood Programs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {livelihoodPrograms?.map((program) => (
              <div key={program?.id} className="bg-[var(--color-card)] rounded-lg p-6 shadow-conservation hover:shadow-conservation-lg transition-all border border-[var(--color-border)]">
                <div className="bg-[var(--color-primary)]/10 p-3 rounded-lg w-fit mb-4">
                  <Icon name={program?.icon} size={24} className="text-[var(--color-primary)]" />
                </div>
                <h4 className="text-lg font-bold text-[var(--color-foreground)] mb-2">{program?.name}</h4>
                <p className="text-sm text-[var(--color-muted-foreground)] mb-4">{program?.description}</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--color-muted-foreground)]">Participants:</span>
                    <span className="font-semibold text-[var(--color-foreground)]">{program?.participants}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--color-muted-foreground)]">Avg Income:</span>
                    <span className="font-semibold text-success">{program?.avgIncome}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--color-muted-foreground)]">Jobs Created:</span>
                    <span className="font-semibold text-[var(--color-foreground)]">{program?.jobs}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Success Stories */}
        <div className="bg-gradient-conservation rounded-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto text-center">
            <Icon name="Quote" size={48} className="text-accent mx-auto mb-6" />
            <blockquote className="text-xl text-white mb-6 italic">
              "Before joining the eco-tourism guide program, I earned ₱8,000 monthly from irregular fishing. Now, as a certified guide, I make ₱18,500 consistently, and I'm teaching my children that protecting nature is protecting our future."
            </blockquote>
            <div className="text-gray-300">
              <div className="font-medium">Ricardo Santos</div>
              <div className="text-sm">Community Ranger & Eco-Tourism Guide</div>
            </div>
          </div>
        </div>

        {/* Economic Opportunities */}
        <div className="bg-card rounded-lg p-8 shadow-conservation">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            Available Economic Opportunities
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-background rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="GraduationCap" size={24} className="text-success" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">Skills Training Programs</h4>
                  <p className="text-sm text-gray-300 mb-3">
                    Free vocational training in eco-tourism, sustainable agriculture, handicraft production, and conservation skills.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-success">
                    <Icon name="CheckCircle2" size={16} />
                    <span>Next intake: January 2026</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-background rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="DollarSign" size={24} className="text-accent" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">Microfinance Support</h4>
                  <p className="text-sm text-gray-300 mb-3">
                    Low-interest loans for conservation-based enterprises, with business mentorship and market access support.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-accent">
                    <Icon name="CheckCircle2" size={16} />
                    <span>Applications open year-round</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-background rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-info/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Store" size={24} className="text-info" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">Market Access Programs</h4>
                  <p className="text-sm text-gray-300 mb-3">
                    Direct market connections for sustainable products through visitor center sales, online platforms, and urban partnerships.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-info">
                    <Icon name="CheckCircle2" size={16} />
                    <span>Vendor applications accepted</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-background rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Users" size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">Cooperative Formation</h4>
                  <p className="text-sm text-gray-300 mb-3">
                    Support for establishing community cooperatives with legal assistance, governance training, and startup capital.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <Icon name="CheckCircle2" size={16} />
                    <span>Consultation available</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-300 mb-6">
              Ready to explore economic opportunities through conservation?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors">
                Apply for Training
              </button>
              <button className="px-6 py-3 bg-transparent border border-primary text-primary rounded-lg font-medium hover:bg-primary/10 transition-colors">
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EconomicImpact;